import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface DiscoveryGroup {
  id: string;
  name: string;
  description: string | null;
  location: string | null;
  memberCount: number;
  skillRange: string;
  avatarUrl: string | null;
  isPublic: boolean;
  lastActivity: string | null;
  score: number;
  matchReasons: string[];
}

export interface DiscoveryPlayer {
  id: string;
  full_name: string;
  avatar_url: string | null;
  skill_rating: number;
  play_type: string | null;
  preferred_position: string | null;
  score: number;
  matchReasons: string[];
}

export interface DiscoveryMatch {
  id: string;
  date: string;
  clubName: string | null;
  clubAddress: string | null;
  spotsAvailable: number;
  playerCount: number;
  score: number;
  matchReasons: string[];
}

export interface DiscoveryTournament {
  id: string;
  name: string;
  date: string;
  registrationDeadline: string;
  groupName: string | null;
  clubName: string | null;
  tournamentType: string;
  score: number;
  matchReasons: string[];
}

export interface DiscoveryResults {
  groups: DiscoveryGroup[];
  players: DiscoveryPlayer[];
  openMatches: DiscoveryMatch[];
  tournaments: DiscoveryTournament[];
}

export function useDiscoveryEngine() {
  const { user, profile } = useAuth();

  return useQuery<DiscoveryResults>({
    queryKey: ['discovery-engine', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('community-discover');
      if (error) {
        // Auth/session hiccups (e.g. 401) must never blank the page — show no suggestions instead
        console.warn('[discovery] community-discover failed:', error.message);
        return { players: [], openMatches: [], tournaments: [] } as DiscoveryResults;
      }
      return data as DiscoveryResults;
    },
    enabled: !!user && !!profile,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
}
